import React, { useState, useRef, useEffect } from 'react';
import { Calendar, ChevronDown, ArrowLeft, ArrowRight, X } from 'lucide-react';

interface DateRangeSelectorProps {
  onApply: (startDate: string, endDate: string, viewMode: 'day' | 'week' | 'month') => void;
  initialStartDate?: string;
  initialEndDate?: string;
  placeholder?: string;
  initialViewMode?: 'day' | 'week' | 'month';
}

const DateRangeSelector: React.FC<DateRangeSelectorProps> = ({
  onApply,
  initialStartDate = '',
  initialEndDate = '',
  placeholder = 'Select Period',
  initialViewMode = 'day'
}) => {
  // Модальное окно
  const [isModalOpen, setIsModalOpen] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);
  
  // View mode (day, week или month)
  const [viewMode, setViewMode] = useState<'day' | 'week' | 'month'>(initialViewMode);
  
  // Текущее состояние календаря
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [nextMonth, setNextMonth] = useState(new Date(new Date().setMonth(new Date().getMonth() + 1)));
  
  // Текущий выбор дат
  const [startDate, setStartDate] = useState<string>(initialStartDate);
  const [endDate, setEndDate] = useState<string>(initialEndDate);
  
  // Временный выбор (до применения)
  const [tempStartDate, setTempStartDate] = useState<string>(initialStartDate);
  const [tempEndDate, setTempEndDate] = useState<string>(initialEndDate);
  const [hoveredDate, setHoveredDate] = useState<Date | null>(null);
  
  // Текущий тип выбора
  const [rangeType, setRangeType] = useState<string>('custom');
  
  // Отображаемый текст
  const [displayText, setDisplayText] = useState(placeholder);

  // Обновление отображаемого текста при изменении дат
  useEffect(() => {
    if (startDate && endDate) {
      if (startDate === endDate) {
        setDisplayText(formatDateDisplay(startDate));
      } else {
        setDisplayText(`${formatDateDisplay(startDate)} - ${formatDateDisplay(endDate)}`);
      }
    } else {
      setDisplayText(placeholder);
    }
  }, [startDate, endDate, placeholder]);

  // Закрытие по клику вне модального окна
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        setIsModalOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  // Форматирование даты для отображения
  const formatDateDisplay = (dateStr: string): string => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return viewMode === 'month' 
      ? date.toLocaleDateString('ru-RU', { 
          month: '2-digit', 
          year: 'numeric' 
        })
      : date.toLocaleDateString('ru-RU', { 
          day: '2-digit', 
          month: '2-digit', 
          year: 'numeric' 
        });
  };

  // Форматирование даты для API в зависимости от режима
  const formatDateForAPI = (date: Date): string => {
    if (viewMode === 'day' || viewMode === 'week') {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    } else {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      return `${year}-${month}`;
    }
  };

  // Навигация по календарю
  const goToPreviousMonth = () => {
    const prevMonth = new Date(currentMonth);
    prevMonth.setMonth(prevMonth.getMonth() - 1);
    setCurrentMonth(prevMonth);
    
    const prevNextMonth = new Date(nextMonth);
    prevNextMonth.setMonth(prevNextMonth.getMonth() - 1);
    setNextMonth(prevNextMonth);
  };

  const goToNextMonth = () => {
    const nextMonthDate = new Date(currentMonth);
    nextMonthDate.setMonth(nextMonthDate.getMonth() + 1);
    setCurrentMonth(nextMonthDate);
    
    const nextNextMonth = new Date(nextMonth);
    nextNextMonth.setMonth(nextNextMonth.getMonth() + 1);
    setNextMonth(nextNextMonth);
  };

  // Получение предустановленного диапазона дат
  const getPresetDateRange = (presetType: string, mode: 'day' | 'week' | 'month') => {
    const today = new Date();
    let startDate = new Date();
    let endDate = new Date();

    if (mode === 'day' || mode === 'week') {
      switch (presetType) {
        case 'last7days':
          startDate.setDate(today.getDate() - 6);
          break;
        case 'lastMonth':
          startDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
          endDate = new Date(today.getFullYear(), today.getMonth(), 0);
          break;
        case 'last28days':
          startDate.setDate(today.getDate() - 27);
          break;
        case 'last3months':
          startDate.setMonth(today.getMonth() - 2);
          break;
        case 'last6months':
          startDate.setMonth(today.getMonth() - 5);
          break;
        case 'lastYear':
          startDate.setFullYear(today.getFullYear() - 1);
          break;
        default:
          // По умолчанию не меняем даты
          break;
      }
    } else {
      // Month mode presets
      switch (presetType) {
        case 'lastMonth':
          startDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
          endDate = new Date(today.getFullYear(), today.getMonth(), 0);
          break;
        case 'last3months':
          startDate = new Date(today.getFullYear(), today.getMonth() - 2, 1);
          break;
        case 'last6months':
          startDate = new Date(today.getFullYear(), today.getMonth() - 5, 1);
          break;
        case 'lastYear':
          startDate.setFullYear(today.getFullYear() - 1);
          endDate = new Date(today.getFullYear(), today.getMonth(), 0);
          break;
        default:
          // По умолчанию не меняем даты
          break;
      }
    }

    return {
      startDate: formatDateForAPI(startDate),
      endDate: formatDateForAPI(endDate)
    };
  };

  // Выбор предустановленного периода
  const selectPresetRange = (presetType: string) => {
    setRangeType(presetType);
    
    if (presetType === 'custom') {
      return; // Для произвольного периода не меняем даты
    }
    
    const { startDate, endDate } = getPresetDateRange(presetType, viewMode);
    setTempStartDate(startDate);
    setTempEndDate(endDate || startDate);
  };

  // Генерация дней календаря
  const generateCalendarDays = (month: Date) => {
    const year = month.getFullYear();
    const monthIndex = month.getMonth();
    
    // Получаем первый день месяца
    const firstDay = new Date(year, monthIndex, 1);
    const startingDayOfWeek = firstDay.getDay() === 0 ? 6 : firstDay.getDay() - 1; // 0 = Воскресенье, 1 = Понедельник и т.д.
    
    // Получаем количество дней в месяце
    const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();
    
    // Получаем количество дней в предыдущем месяце
    const daysInPrevMonth = new Date(year, monthIndex, 0).getDate();
    
    const days = [];
    
    // Добавляем дни из предыдущего месяца
    for (let i = startingDayOfWeek - 1; i >= 0; i--) {
      const date = new Date(year, monthIndex - 1, daysInPrevMonth - i);
      days.push({
        date,
        isCurrentMonth: false,
      });
    }
    
    // Добавляем дни текущего месяца
    for (let i = 1; i <= daysInMonth; i++) {
      const date = new Date(year, monthIndex, i);
      days.push({
        date,
        isCurrentMonth: true,
      });
    }
    
    // Добавляем дни следующего месяца, чтобы заполнить оставшиеся ячейки
    const remainingSlots = 42 - days.length;
    for (let i = 1; i <= remainingSlots; i++) {
      const date = new Date(year, monthIndex + 1, i);
      days.push({
        date,
        isCurrentMonth: false,
      });
    }
    
    return days;
  };

  // Обработка клика по дате - одинаковое поведение для режимов день и неделя
  const handleDateClick = (date: Date) => {
    const formattedDate = formatDateForAPI(date);
    
    if (!tempStartDate || (tempStartDate && tempEndDate)) {
      // Начинаем новый выбор
      setTempStartDate(formattedDate);
      setTempEndDate('');
      setRangeType('custom');
    } else {
      // Завершаем выбор
      const startDateObj = new Date(tempStartDate);
      if (date < startDateObj) {
        setTempEndDate(tempStartDate);
        setTempStartDate(formattedDate);
      } else {
        setTempEndDate(formattedDate);
      }
    }
  };

  // Обработка наведения на дату
  const handleDateHover = (date: Date) => {
    if (tempStartDate && !tempEndDate) {
      setHoveredDate(date);
    }
  };

  // Проверка, находится ли дата в выбранном диапазоне
  const isInRange = (date: Date) => {
    if (!tempStartDate) return false;
    const startDateObj = new Date(tempStartDate);
    
    if (tempEndDate) {
      const endDateObj = new Date(tempEndDate);
      return date >= startDateObj && date <= endDateObj;
    }
    if (hoveredDate) {
      return (
        (date >= startDateObj && date <= hoveredDate) ||
        (date >= hoveredDate && date <= startDateObj)
      );
    }
    return false;
  };

  // Проверка, является ли дата одной из границ диапазона
  const isRangeEnd = (date: Date) => {
    if (!tempStartDate) return false;
    const startDateObj = new Date(tempStartDate);
    
    // Проверяем и для случая когда выбрана только одна дата
    if (!tempEndDate) {
      return date.getTime() === startDateObj.getTime();
    }
    
    return (
      date.getTime() === startDateObj.getTime() ||
      (tempEndDate && date.getTime() === new Date(tempEndDate).getTime())
    );
  };

  // Применение выбранного диапазона
  const handleApply = () => {
    if (tempStartDate) {
      const finalEndDate = tempEndDate || tempStartDate; // Если конец не выбран, используем начало
      setStartDate(tempStartDate);
      setEndDate(finalEndDate);
      onApply(tempStartDate, finalEndDate, viewMode);
      setIsModalOpen(false);
    }
  };

  // Отмена выбора
  const handleCancel = () => {
    setTempStartDate(startDate);
    setTempEndDate(endDate);
    setIsModalOpen(false);
  };

  const goToPreviousYear = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear() - 1, currentMonth.getMonth()));
  };
  
  const goToNextYear = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear() + 1, currentMonth.getMonth()));
  };

  // Дни недели
  const dayNames = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];

  return (
    <div style={{ position: 'relative', width: '100%' }}>
      {/* Кнопка для открытия модального окна */}
      <button
        onClick={() => setIsModalOpen(true)}
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '8px 12px',
          border: '1px solid #dee2e6',
          borderRadius: '6px',
          cursor: 'pointer',
          backgroundColor: '#fff',
          width: '100%',
          textAlign: 'left',
          fontSize: '14px',
        }}
      >
        <span>{displayText}</span>
        <Calendar size={16} />
      </button>

      {/* Модальное окно выбора дат */}
      {isModalOpen && (
        <div className="modal-overlay" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div
            ref={modalRef}
            className="modal-content"
            style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              width: '90%',
              maxWidth: '700px',
              maxHeight: '85vh',
              padding: '20px',
              boxShadow: '0 5px 15px rgba(0, 0, 0, 0.3)',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'auto',
            }}
          >
            {/* Заголовок модального окна */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px',
            }}>
              <h3 style={{ margin: 0, color: '#2c3e50' }}>Выберите период</h3>
              <button
                onClick={() => setIsModalOpen(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#6c757d',
                }}
              >
                <X size={20} />
              </button>
            </div>
            
            {/* View Mode Toggle - Добавляем Week между Day и Month */}
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              marginBottom: '20px',
              backgroundColor: '#f1f3f5',
              borderRadius: '8px',
              overflow: 'hidden',
              border: '1px solid #dee2e6'
            }}>
              <button
                onClick={() => {
                  setViewMode('day');
                  // Reset dates when switching to day mode
                  setTempStartDate('');
                  setTempEndDate('');
                }}
                style={{
                  flex: 1,
                  padding: '10px 16px',
                  backgroundColor: viewMode === 'day' ? '#3498db' : 'transparent',
                  color: viewMode === 'day' ? '#fff' : '#495057',
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  fontWeight: viewMode === 'day' ? 600 : 'normal',
                  borderRadius: '6px'
                }}
              >
                Day
              </button>
              <button
                onClick={() => {
                  setViewMode('week');
                  // Reset dates when switching to week mode
                  setTempStartDate('');
                  setTempEndDate('');
                }}
                style={{
                  flex: 1,
                  padding: '10px 16px',
                  backgroundColor: viewMode === 'week' ? '#3498db' : 'transparent',
                  color: viewMode === 'week' ? '#fff' : '#495057',
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  fontWeight: viewMode === 'week' ? 600 : 'normal',
                  borderRadius: '6px'
                }}
              >
                Week
              </button>
              <button
                onClick={() => {
                  setViewMode('month');
                  // Reset dates when switching to month mode
                  setTempStartDate('');
                  setTempEndDate('');
                }}
                style={{
                  flex: 1,
                  padding: '10px 16px',
                  backgroundColor: viewMode === 'month' ? '#3498db' : 'transparent',
                  color: viewMode === 'month' ? '#fff' : '#495057',
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  fontWeight: viewMode === 'month' ? 600 : 'normal',
                  borderRadius: '6px'
                }}
              >
                Month
              </button>
            </div>
            
            {/* Поля ввода дат */}
            <div style={{
              display: 'flex',
              gap: '10px',
              marginBottom: '20px',
            }}>
              <div style={{ flex: 1 }}>
                <label style={{
                  display: 'block',
                  marginBottom: '5px',
                  fontSize: '14px',
                  color: '#495057',
                }}>
                  {viewMode === 'month' ? 'Start Month' : viewMode === 'week' ? 'Start Date' : 'Start Date'}
                </label>
                <input
                  type={viewMode === 'month' ? 'month' : 'date'}
                  value={tempStartDate}
                  onChange={(e) => {
                    setTempStartDate(e.target.value);
                    setRangeType('custom');
                  }}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #dee2e6',
                    borderRadius: '4px',
                  }}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={{
                  display: 'block',
                  marginBottom: '5px',
                  fontSize: '14px',
                  color: '#495057',
                }}>
                  {viewMode === 'month' ? 'End Month' : viewMode === 'week' ? 'End Date' : 'End Date'}
                </label>
                <input
                  type={viewMode === 'month' ? 'month' : 'date'}
                  value={tempEndDate}
                  onChange={(e) => {
                    setTempEndDate(e.target.value);
                    setRangeType('custom');
                  }}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #dee2e6',
                    borderRadius: '4px',
                  }}
                />
              </div>
            </div>
            
            {/* Календарь */}
            <div style={{
              display: 'flex',
              flexDirection: 'row',
              gap: '20px',
              flexWrap: 'wrap',
            }}>
              {/* Первый месяц */}
              <div style={{ minWidth: '280px', flex: 1 }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '10px',
                }}>
                  <button
                    onClick={viewMode === 'month' ? goToPreviousYear : goToPreviousMonth}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      color: '#6c757d',
                    }}
                  >
                    <ArrowLeft size={16} />
                  </button>
                  <span style={{ fontWeight: 500 }}>
                    {viewMode === 'month'
                      ? currentMonth.getFullYear()
                      : currentMonth.toLocaleDateString('ru-RU', { month: 'long', year: 'numeric' })}
                  </span>
                  <button
                    onClick={viewMode === 'month' ? goToNextYear : goToNextMonth}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      color: '#6c757d',
                    }}
                  >
                    <ArrowRight size={16} />
                  </button>
                </div>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(7, 1fr)',
                  gap: '2px',
                }}>
                  {/* Дни недели */}
                  {dayNames.map((day) => (
                    <div
                      key={day}
                      style={{
                        textAlign: 'center',
                        padding: '5px',
                        fontSize: '12px',
                        fontWeight: 500,
                        color: '#6c757d',
                      }}
                    >
                      {day}
                    </div>
                  ))}
                  
                  {/* Дни календаря */}
                  {viewMode === 'day' || viewMode === 'week' ? (
                    generateCalendarDays(currentMonth).map((day, index) => {
                      const formattedDate = formatDateForAPI(day.date);
                      const isSelected = formattedDate === tempStartDate || formattedDate === tempEndDate;
                      
                      return (
                        <div
                          key={`current-${index}`}
                          onClick={() => handleDateClick(day.date)}
                          onMouseEnter={() => handleDateHover(day.date)}
                          style={{
                            textAlign: 'center',
                            padding: '8px 5px',
                            cursor: 'pointer',
                            backgroundColor: isSelected
                              ? '#3498db'
                              : isInRange(day.date)
                              ? '#e1f0fa'
                              : 'transparent',
                            color: isSelected
                              ? 'white'
                              : !day.isCurrentMonth
                              ? '#adb5bd'
                              : '#495057',
                            borderRadius: '4px',
                            fontWeight: isSelected ? 500 : 'normal',
                          }}
                        >
                          {day.date.getDate()}
                        </div>
                      );
                    })
                  ) : (
                    // Month selection for month mode
                    Array.from({ length: 12 }, (_, i) => {
                      const monthDate = new Date(currentMonth.getFullYear(), i);
                      const formattedMonthDate = formatDateForAPI(monthDate);
                      return (
                        <div
                          key={`month-${i}`}
                          onClick={() => handleDateClick(monthDate)}
                          style={{
                            textAlign: 'center',
                            padding: '8px 5px',
                            cursor: 'pointer',
                            backgroundColor: 
                              formattedMonthDate === tempStartDate || formattedMonthDate === tempEndDate
                                ? '#3498db'
                                : (tempStartDate && tempEndDate && 
                                   formattedMonthDate >= tempStartDate && 
                                   formattedMonthDate <= tempEndDate)
                                ? '#e1f0fa'
                                : 'transparent',
                            color: 
                              formattedMonthDate === tempStartDate || formattedMonthDate === tempEndDate
                                ? 'white'
                                : '#495057',
                            borderRadius: '4px',
                            fontWeight: 
                              formattedMonthDate === tempStartDate || formattedMonthDate === tempEndDate 
                                ? 500 
                                : 'normal',
                          }}
                        >
                          {monthDate.toLocaleString('default', { month: 'short' })}
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
              
              {/* Второй месяц (отображается только в режимах day и week) */}
              {(viewMode === 'day' || viewMode === 'week') && (
                <div style={{ minWidth: '280px', flex: 1 }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '10px',
                  }}>
                    <div style={{ width: '16px' }} /> {/* Пустой div для выравнивания */}
                    <span style={{ fontWeight: 500 }}>
                      {nextMonth.toLocaleDateString('ru-RU', { month: 'long', year: 'numeric' })}
                    </span>
                    <div style={{ width: '16px' }} /> {/* Пустой div для выравнивания */}
                  </div>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(7, 1fr)',
                    gap: '2px',
                  }}>
                    {/* Дни недели */}
                    {dayNames.map((day) => (
                      <div
                        key={day}
                        style={{
                          textAlign: 'center',
                          padding: '5px',
                          fontSize: '12px',
                          fontWeight: 500,
                          color: '#6c757d',
                        }}
                      >
                        {day}
                      </div>
                    ))}
                    
                    {/* Дни календаря следующего месяца */}
                    {generateCalendarDays(nextMonth).map((day, index) => {
                      const formattedDate = formatDateForAPI(day.date);
                      const isSelected = formattedDate === tempStartDate || formattedDate === tempEndDate;
                      
                      return (
                        <div
                          key={`next-${index}`}
                          onClick={() => handleDateClick(day.date)}
                          onMouseEnter={() => handleDateHover(day.date)}
                          style={{
                            textAlign: 'center',
                            padding: '8px 5px',
                            cursor: 'pointer',
                            backgroundColor: isSelected
                              ? '#3498db'
                              : isInRange(day.date)
                              ? '#e1f0fa'
                              : 'transparent',
                            color: isSelected
                              ? 'white'
                              : !day.isCurrentMonth
                              ? '#adb5bd'
                              : '#495057',
                            borderRadius: '4px',
                            fontWeight: isSelected ? 500 : 'normal',
                          }}
                        >
                          {day.date.getDate()}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
            
            {/* Нижняя панель с кнопками */}
            <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              marginTop: '20px',
              padding: '15px 0 0',
              borderTop: '1px solid #e9ecef',
              gap: '10px',
            }}>
              <button
                onClick={handleCancel}
                style={{
                  padding: '8px 16px',
                  border: '1px solid #dee2e6',
                  borderRadius: '4px',
                  backgroundColor: '#f8f9fa',
                  cursor: 'pointer',
                }}
              >
                Отмена
              </button>
              <button
                onClick={handleApply}
                disabled={!tempStartDate}
                style={{
                  padding: '8px 16px',
                  border: 'none',
                  borderRadius: '4px',
                  backgroundColor: tempStartDate ? '#3498db' : '#adb5bd',
                  color: 'white',
                  cursor: tempStartDate ? 'pointer' : 'not-allowed',
                }}
              >
                Применить
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DateRangeSelector;